import parse from 'html-react-parser';
import { useState } from 'react';
import {
  RELEASE_NOTES,
  type ReleaseNoteItem,
  getLatestReleaseNotes,
} from 'src/release-notes';

import { DialogWrapper } from './shared/dialog-wrapper';

interface DialogReleaseNotesProps {
  onClose: () => void;
}

function SingleVersionBlock({
  item,
  attachTestIds = false,
}: {
  item: ReleaseNoteItem;
  attachTestIds?: boolean;
}) {
  const { sections } = item;
  if (sections?.length) {
    return (
      <div className="space-y-4">
        {sections.map((section, sectionIndex) => {
          const changeIndexOffset = sections
            .slice(0, sectionIndex)
            .reduce((sum, s) => sum + s.items.length, 0);
          return (
            <div key={section.title} className="space-y-1">
              <h5 className="text-sm font-semibold text-gray-900">
                {section.title}
              </h5>
              <ul className="list-inside list-disc space-y-1 text-sm text-gray-700">
                {section.items.map((line, itemIndex) => {
                  const i = changeIndexOffset + itemIndex;
                  return (
                    <li
                      key={`${section.title}-${i}`}
                      {...(attachTestIds && {
                        'data-testid': `release-notes-change-${i}`,
                      })}
                    >
                      {parse(line)}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  const changes = item.changes ?? [];
  return (
    <div className="space-y-2">
      <ul className="list-inside list-disc space-y-1 text-sm text-gray-700">
        {changes.map((line, i) => (
          <li
            key={i}
            {...(attachTestIds && {
              'data-testid': `release-notes-change-${i}`,
            })}
          >
            {parse(line)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReleaseNotesContent({
  item,
  attachTestIds = false,
}: {
  item: ReleaseNoteItem;
  attachTestIds?: boolean;
}) {
  return (
    <div
      className="max-h-[60vh] space-y-2 overflow-y-auto pr-2"
      data-testid="release-notes-content"
    >
      <SingleVersionBlock item={item} attachTestIds={attachTestIds} />
    </div>
  );
}

function FullHistoryContent() {
  return (
    <div
      className="max-h-[60vh] space-y-6 overflow-y-auto pr-2"
      data-testid="release-notes-full-history"
    >
      {RELEASE_NOTES.map((item) => (
        <section key={item.version}>
          <h4 className="text-base font-semibold text-gray-900">
            Version {item.version}
            {item.date && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({item.date})
              </span>
            )}
          </h4>
          <SingleVersionBlock item={item} />
        </section>
      ))}
    </div>
  );
}

export default function DialogReleaseNotes({
  onClose,
}: DialogReleaseNotesProps) {
  const [viewAll, setViewAll] = useState(false);
  const latest = getLatestReleaseNotes();

  const footer = viewAll ? (
    <button
      type="button"
      data-testid="release-notes-show-latest"
      onClick={() => setViewAll(false)}
      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
    >
      Show latest only
    </button>
  ) : (
    <button
      type="button"
      data-testid="release-notes-view-all"
      onClick={() => setViewAll(true)}
      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
    >
      View full history
    </button>
  );

  return (
    <DialogWrapper
      title={
        viewAll
          ? 'Release notes – Full history'
          : `Version ${latest.version} – Release Notes`
      }
      onClose={onClose}
      testId="dialog-release-notes"
      panelClassName="max-w-3xl"
      footer={footer}
    >
      {viewAll ? (
        <FullHistoryContent />
      ) : (
        <ReleaseNotesContent item={latest} attachTestIds />
      )}
    </DialogWrapper>
  );
}
